import { useState } from 'react'
import type { VotePoll } from '../data/articles'

interface VoteWidgetProps {
  poll: VotePoll
  compact?: boolean
}

// "Prepper Consensus" vote widget. Percentages are illustrative (seeded from
// baseSplit) until real vote storage exists on the backend — see the
// mitohacker-content plugin's vote endpoints for the pattern to port over.
export default function VoteWidget({ poll, compact }: VoteWidgetProps) {
  const [picked, setPicked] = useState<number | null>(null)
  const [tally, setTally] = useState(poll.startingVotes)

  const percentages = (() => {
    if (picked === null) return poll.options.map(() => 0)
    const vals = poll.baseSplit.slice()
    vals[picked] += 2
    const sum = vals.reduce((a, c) => a + c, 0)
    return vals.map((v) => Math.round((v / sum) * 100))
  })()
  const max = Math.max(...percentages)
  const voted = picked !== null

  function cast(i: number) {
    if (voted) return
    setPicked(i)
    setTally((t) => t + 1)
  }

  return (
    <div className={`vote${voted ? ' voted' : ''}`} style={compact ? { borderTop: 0, paddingTop: 0 } : undefined}>
      {!compact && (
        <div className="vhd">
          <span className="dot" aria-hidden="true"></span>
          <span className="t">Prepper Consensus</span>
        </div>
      )}
      <p className="q" style={compact ? { fontSize: 15 } : undefined}>
        {poll.question}
      </p>
      <div className="opts">
        {poll.options.map((opt, i) => (
          <button
            key={opt}
            className={`vbtn${picked === i ? ' picked' : ''}${voted && percentages[i] === max ? ' win' : ''}`}
            onClick={() => cast(i)}
            aria-pressed={picked === i}
            aria-label={voted ? `${opt}, ${percentages[i]} percent, ${tally.toLocaleString()} votes` : opt}
          >
            <span className="fill" style={{ width: voted ? `${percentages[i]}%` : 0 }} aria-hidden="true"></span>
            <span className="lab" aria-hidden="true">
              <span>{opt}</span>
              <span className="pct">{percentages[i]}%</span>
            </span>
          </button>
        ))}
      </div>
      <div className="tally">
        <span>{voted ? 'Thanks, see the split' : 'Tap to cast your vote'}</span>
        <span className="tnum">{tally.toLocaleString()} votes</span>
      </div>
      <div aria-live="polite" className="sr-only">
        {voted ? `You voted ${poll.options[picked!]}. Current split: ${poll.options
          .map((opt, i) => `${opt} ${percentages[i]} percent`)
          .join(', ')}.` : ''}
      </div>
    </div>
  )
}
